import { useCallback, useEffect, useMemo, useState } from 'react';

import { AppError } from '../../api/errors.js';
import { useDataHubClient } from '../../context/DataHubClientContext.js';
import { AccessControlService } from '../../services/accessControlService.js';
import type { Dataset } from '../../services/dataHubService.interface.js';
import { DataHubService } from '../../services/dataHubService.js';

export interface UseDatasetResult {
  dataset: Dataset | null;
  isLoading: boolean;
  error: string | null;
  /** HTTP status code when the error originated from an API response. */
  errorStatusCode: number | undefined;
  accessRequestUrl: string | null;
  refetch: () => void;
}

export function useDataset(dataSetId: string): UseDatasetResult {
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);
  const accessControlService = useMemo(
    () => new AccessControlService(client),
    [client],
  );

  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatusCode, setErrorStatusCode] = useState<number | undefined>(
    undefined,
  );
  const [accessRequestUrl, setAccessRequestUrl] = useState<string | null>(null);
  const [fetchTick, setFetchTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const isRefetch = dataset !== null && dataset.id === dataSetId;
    if (!isRefetch) {
      setIsLoading(true);
    }
    setError(null);
    setErrorStatusCode(undefined);
    setAccessRequestUrl(null);

    void (async () => {
      try {
        const result = await service.getDataset(dataSetId);
        if (cancelled) return;
        setDataset(result);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load dataset');
        setErrorStatusCode(
          err instanceof AppError ? err.statusCode : undefined,
        );

        if (err instanceof AppError && err.statusCode === 403) {
          try {
            const result =
              await accessControlService.getDatasetAccessRequestUrl(dataSetId);
            if (!cancelled) {
              setAccessRequestUrl(result.accessRequestUrl ?? null);
            }
          } catch {
            if (!cancelled) setAccessRequestUrl(null);
          }
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, accessControlService, dataSetId, fetchTick]);

  const refetch = useCallback(() => setFetchTick((t) => t + 1), []);

  return {
    dataset,
    isLoading,
    error,
    errorStatusCode,
    accessRequestUrl,
    refetch,
  };
}
